import { colors } from '../../../colors';
import InputField from '../../../components/Form/InputField';
import { FaSearch, FaTimes } from 'react-icons/fa';
import LoadingIcons from 'react-loading-icons';
import { useCallback, useMemo, useState } from 'react';
import { AssetTypeFilter, SortBy } from '../types';
import './index.css';

interface SortOption {
  value: SortBy;
  label: string;
}

interface AssetTypeOption {
  value: AssetTypeFilter;
  label: string;
}

interface Props {
  title: string;
  query: string | undefined;
  setQuery: React.Dispatch<React.SetStateAction<string | undefined>>;
  isLoading: boolean;
  credits: number | undefined;
  sortBy?: SortBy;
  setSortBy?: (value: SortBy) => void;
  sortOptions?: SortOption[];
  assetTypeFilter?: AssetTypeFilter;
  setAssetTypeFilter?: (value: AssetTypeFilter) => void;
  assetTypeOptions?: AssetTypeOption[];
}

const DiscoverHeader = (props: Props) => {
  const {
    title,
    query,
    setQuery,
    isLoading,
    credits,
    sortBy,
    setSortBy,
    sortOptions,
    assetTypeFilter,
    setAssetTypeFilter,
    assetTypeOptions,
  } = props;

  const [searchQuery, setSearchQuery] = useState<string | undefined>(query);

  // The request to fetch the bots is heavy
  // So we don't want to re fetch them every time the user changes the query
  const handleQueryUpdate = useCallback(() => {
    setQuery(searchQuery);
  }, [searchQuery, setQuery]);

  const handleClear = useCallback(() => {
    setSearchQuery('');
    setQuery(undefined);
  }, [setQuery]);

  const searchField = useMemo(() => {
    return {
      label: 'Search',
      value: searchQuery,
      handleValueChange: (value: string) => {
        if (value.length > 12) {
          return;
        }

        setSearchQuery(value.toUpperCase());
      },
      shouldFit: true,
      handleEnterPress: handleQueryUpdate,
    };
  }, [handleQueryUpdate, searchQuery]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        backgroundColor: colors.black,
        marginLeft: -30,
        marginRight: -30,
        paddingLeft: 30,
        paddingRight: 36,
        paddingBottom: 24,
      }}
    >
      {/* Row 1: title + search input + button */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 64,
          marginTop: 16,
        }}
      >
        <p
          style={{
            color: colors.white,
            fontSize: 32,
            fontWeight: 'bold',
            margin: 0,
            flexShrink: 0,
          }}
        >
          {title}
        </p>
        <div
          className="discover-search-field"
          style={{
            display: 'flex',
            alignItems: 'center',
            flex: '1 1 200px',
            minWidth: 200,
          }}
        >
          <div
            style={{
              position: 'relative',
              flex: 1,
              display: 'flex',
              minWidth: 0,
            }}
          >
            <InputField config={searchField} width={'100%'} />
            {!!searchQuery && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={handleClear}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  padding: 4,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.6,
                }}
              >
                <FaTimes color={colors.white} size={14} />
              </button>
            )}
          </div>
          <button
            onClick={handleQueryUpdate}
            style={{
              backgroundColor: '#0047AB',
              border: '1px solid #0047AB',
              borderRadius: 4,
              marginLeft: 12,
              cursor: 'pointer',
              height: 56,
              aspectRatio: 1,
              flexShrink: 0,
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <LoadingIcons.Grid width={20} height={20} />
            ) : (
              <FaSearch color={colors.white} size={20} />
            )}
          </button>
        </div>
      </div>

      {/* Row 2: credits (left) + Sort/Type pill groups (right), wrap when narrow */}
      {(typeof credits !== 'undefined' ||
        sortBy !== undefined ||
        assetTypeFilter !== undefined) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 28,
            marginTop: 10,
          }}
        >
          {typeof credits !== 'undefined' && (
            <p
              style={{
                margin: 0,
                padding: 0,
                color: colors.white,
              }}
            >
              Available credits:{' '}
              <span style={{ color: credits < 0 ? colors.red : colors.green }}>
                {credits}
              </span>
              /50
            </p>
          )}
          {sortBy !== undefined && setSortBy && sortOptions && (
            <div className="discover-sort">
              <span className="discover-sort-label">Sort by</span>
              <div className="pill-toggle" role="group" aria-label="Sort bots">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={sortBy === opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`pill-toggle-btn${
                      sortBy === opt.value ? ' pill-toggle-btn--active' : ''
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {assetTypeFilter !== undefined &&
            setAssetTypeFilter &&
            assetTypeOptions && (
              <div className="discover-sort">
                <span className="discover-sort-label">Type</span>
                <div
                  className="pill-toggle"
                  role="group"
                  aria-label="Filter by asset type"
                >
                  {assetTypeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      aria-pressed={assetTypeFilter === opt.value}
                      onClick={() => setAssetTypeFilter(opt.value)}
                      className={`pill-toggle-btn${
                        assetTypeFilter === opt.value
                          ? ' pill-toggle-btn--active'
                          : ''
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default DiscoverHeader;
